/**
 * Utility functions for consistent analytics tracking
 */

interface TrackingProperties {
  companyCode?: string;
  landingCaseId?: string;
  caseId?: string;
  userId?: string;
  [key: string]: any;
}

/**
 * Extract company code from route parameters or props
 * @param companyId - The company ID from route params or props
 * @returns The company code or 'General' if not available
 */
export const getCompanyCode = (companyId?: string | null): string => {
  return companyId || 'General';
};

/**
 * Extract landing case ID from route parameters
 * @param caseId - The case ID from route params
 * @returns The case ID if available, undefined otherwise
 */
export const getLandingCaseId = (caseId?: string | null): string | undefined => {
  return caseId || undefined;
};

/**
 * Build consistent tracking properties for landing page events
 * @param companyId - The company ID
 * @param caseId - The case ID (optional)
 * @param additionalProps - Any additional properties to include
 * @returns Formatted tracking properties object
 */
export const buildLandingPageTrackingProps = (
  companyId?: string | null,
  caseId?: string | null,
  additionalProps?: Record<string, any>
): TrackingProperties => {
  const props: TrackingProperties = {
    companyCode: getCompanyCode(companyId),
  };

  const landingCaseId = getLandingCaseId(caseId);
  if (landingCaseId) {
    props.landingCaseId = landingCaseId;
  }

  return {
    ...props,
    ...additionalProps,
  };
};

/**
 * Build tracking properties for identity confirmation events
 * @param isConfirmed - Whether the identity was confirmed successfully
 * @param companyId - The company ID
 * @param caseId - The case ID (optional)
 * @param additionalProps - Any additional properties to include
 * @returns Formatted tracking properties object
 */
export const buildIdentityConfirmationProps = (
  isConfirmed: boolean,
  companyId?: string | null,
  caseId?: string | null,
  additionalProps?: Record<string, any>
): TrackingProperties => {
  return {
    identityConfirmed: isConfirmed,
    ...buildLandingPageTrackingProps(companyId, caseId, additionalProps),
  };
};

/**
 * Build tracking properties for sync status events
 * @param syncStatus - Whether the sync was successful
 * @param caseId - The case ID
 * @param userId - The user ID
 * @param additionalProps - Any additional properties to include
 * @returns Formatted tracking properties object
 */
export const buildSyncStatusProps = (
  syncStatus: boolean,
  caseId?: string,
  userId?: string,
  additionalProps?: Record<string, any>
): TrackingProperties => {
  const props: TrackingProperties = {
    syncStatus,
  };

  if (caseId) props.caseId = caseId;
  if (userId) props.userId = userId;

  return {
    ...props,
    ...additionalProps,
  };
};

/**
 * Build tracking properties for login events
 * @param companyId - The company ID from route or context
 * @param caseId - The case ID from route (optional)
 * @param additionalProps - Any additional properties to include
 * @returns Formatted tracking properties object
 */
export const buildLoginTrackingProps = (
  companyId?: string | null,
  caseId?: string | null,
  additionalProps?: Record<string, any>
): TrackingProperties => {
  return buildLandingPageTrackingProps(companyId, caseId, additionalProps);
};

// Event name constants for consistency
export const TRACKING_EVENTS = {
  LANDING_PAGE_VIEWED: 'Landing Page Viewed',
  IDENTITY_CONFIRMATION: 'Identity Confirmation',
  CREATE_ACCOUNT: 'Create Account',
  LOG_IN_CLICKED: 'Log In Clicked',
  SYNC_STATUS: 'Sync Status',
} as const;

// Health Plan event constants
export const HEALTH_PLAN_EVENTS = {
  HEALTH_PLAN_SELECTED: 'Health Plan Selected',
  HEALTH_PLAN_APPLICATION_STARTED: 'Health Plan Application Started',
  HEALTH_PLAN_APPLICATION_CANCELLED: 'Health Plan Application Cancelled',
  HEALTH_PLAN_APPLICATION_CONTINUED: 'Health Plan Application Continued',
  HEALTH_PLAN_APPLICATION_ENROLLMENT_FORM: 'Health Plan Application Enrollment Form',
  HEALTH_PLAN_APPLICATION: 'Health Plan Application',
  HEALTH_PLAN_APPLICATION_SUBMITTED: 'Health Plan Application Submitted',
} as const;

/**
 * Determine the page view source based on pathname and referrer
 * @param pathname - The current pathname
 * @param referrer - Optional referrer information
 * @returns The page view source
 */
export const getPageViewSource = (pathname: string, referrer?: string): string => {
  if (pathname.includes('/jamie')) return 'Jamie';
  if (pathname.includes('/dashboard')) return 'Dashboard';
  if (pathname.includes('/insurance-plans') || pathname.includes('/health-insurance')) return 'Health Insurance';
  if (referrer?.includes('/jamie')) return 'Jamie';
  if (referrer?.includes('/dashboard')) return 'Dashboard';
  if (referrer?.includes('/insurance-plans') || referrer?.includes('/health-insurance')) return 'Health Insurance';
  return 'Unknown';
};
